import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'
import type { ViewerContext } from '../../context/AuthContext'

export type CorrectiveActionStatus = 'open' | 'in_progress' | 'completed' | 'verified' | 'overdue'
type ViewerRole = ViewerContext['role']

interface ActionVisit {
  visit_id: string
  facility_id: string | null
  status: 'scheduled' | 'in_progress' | 'released' | 'archived' | null
  visit_type?: string | null
}

export interface ActionUserRef {
  user_id: string
  email: string
  role: string | null
}

export interface EvidenceLink {
  action_evidence_link_id: string
  action_id: string
  evidence_id: string
  created_at: string
}

export interface CorrectiveAction {
  action_id: string
  visit_id: string
  title: string
  description: string | null
  owner_role: string | null
  assigned_to_user_id: string | null
  status: CorrectiveActionStatus
  due_date: string | null
  completed_date: string | null
  evidence_required: boolean
  evidence_provided: boolean
  notes: string | null
  visit: ActionVisit | null
  assigned_to: ActionUserRef | null
  verified_by_user: ActionUserRef | null
  evidence_links: EvidenceLink[]
}

export interface UpdateCorrectiveActionInput {
  owner_role?: string | null
  assigned_to_user_id?: string | null
  status?: CorrectiveActionStatus
  due_date?: string | null
  notes?: string | null
}

const ACTION_STATUSES: CorrectiveActionStatus[] = ['open', 'in_progress', 'completed', 'verified', 'overdue']

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asActionStatus(value: unknown): CorrectiveActionStatus {
  if (typeof value === 'string' && ACTION_STATUSES.includes(value as CorrectiveActionStatus)) {
    return value as CorrectiveActionStatus
  }
  return 'open'
}

function asVisit(value: unknown): ActionVisit | null {
  if (!asRecord(value)) return null
  if (typeof value.visit_id !== 'string') return null

  return {
    visit_id: value.visit_id,
    facility_id: typeof value.facility_id === 'string' ? value.facility_id : null,
    visit_type: typeof value.visit_type === 'string' ? value.visit_type : null,
    status:
      value.status === 'scheduled' ||
      value.status === 'in_progress' ||
      value.status === 'released' ||
      value.status === 'archived'
        ? value.status
        : null,
  }
}

function asUserRef(value: unknown): ActionUserRef | null {
  if (!asRecord(value)) return null
  if (typeof value.user_id !== 'string') return null
  if (typeof value.email !== 'string') return null

  return {
    user_id: value.user_id,
    email: value.email,
    role: typeof value.role === 'string' ? value.role : null,
  }
}

function asEvidenceLinks(value: unknown): EvidenceLink[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => {
      if (!asRecord(row)) return null
      if (typeof row.action_evidence_link_id !== 'string') return null
      if (typeof row.action_id !== 'string') return null
      if (typeof row.evidence_id !== 'string') return null
      if (typeof row.created_at !== 'string') return null
      return {
        action_evidence_link_id: row.action_evidence_link_id,
        action_id: row.action_id,
        evidence_id: row.evidence_id,
        created_at: row.created_at,
      }
    })
    .filter((row): row is EvidenceLink => row !== null)
}

function toAction(value: unknown): CorrectiveAction {
  if (!asRecord(value)) throw new Error('Malformed corrective action in server response')
  if (typeof value.action_id !== 'string') throw new Error('Malformed corrective action in server response')
  if (typeof value.visit_id !== 'string') throw new Error('Malformed corrective action in server response')
  if (typeof value.title !== 'string') throw new Error('Malformed corrective action in server response')

  return {
    action_id: value.action_id,
    visit_id: value.visit_id,
    title: value.title,
    description: typeof value.description === 'string' ? value.description : null,
    owner_role: typeof value.owner_role === 'string' ? value.owner_role : null,
    assigned_to_user_id: typeof value.assigned_to_user_id === 'string' ? value.assigned_to_user_id : null,
    status: asActionStatus(value.status),
    due_date: typeof value.due_date === 'string' ? value.due_date : null,
    completed_date: typeof value.completed_date === 'string' ? value.completed_date : null,
    evidence_required: value.evidence_required === true,
    evidence_provided: value.evidence_provided === true,
    notes: typeof value.notes === 'string' ? value.notes : null,
    visit: asVisit(value.visit),
    assigned_to: asUserRef(value.assigned_to),
    verified_by_user: asUserRef(value.verified_by_user),
    evidence_links: asEvidenceLinks(value.evidence_links),
  }
}

export function canMutateAction(
  role: ViewerRole,
  viewerUserId: string | undefined,
  action: Pick<CorrectiveAction, 'assigned_to_user_id'>,
): boolean {
  if (role === 'Admin') return true
  if (!viewerUserId) return false
  return action.assigned_to_user_id === viewerUserId
}

export function canSetActionStatus(role: ViewerRole, status: CorrectiveActionStatus): boolean {
  if (status !== 'verified') return true
  return role === 'Admin'
}

export function isActionMutationLocked(action: Pick<CorrectiveAction, 'status' | 'visit'>): boolean {
  if (action.status === 'verified') return true
  if (action.visit?.status === 'released' || action.visit?.status === 'archived') return true
  return false
}

export async function getCorrectiveActions(filters: { visitId?: string; status?: CorrectiveActionStatus } = {}): Promise<CorrectiveAction[]> {
  const search = new URLSearchParams()
  if (filters.visitId) search.set('visit_id', filters.visitId)
  if (filters.status) search.set('status', filters.status)
  const query = search.toString()

  const { data, error } = await invokeFunction<{ actions: unknown }>('corrective-actions', {
    method: 'GET',
    path: query ? `/?${query}` : '',
  })

  if (error) throw toApiRequestError(error, 'Failed to load corrective actions')
  if (!data || !Array.isArray(data.actions)) throw new Error('Malformed corrective actions response from server')
  return data.actions.map(toAction)
}

export async function getCorrectiveAction(actionId: string): Promise<CorrectiveAction> {
  const { data, error } = await invokeFunction<unknown>('corrective-actions', {
    method: 'GET',
    path: `/${actionId}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load corrective action')
  return toAction(data)
}

export async function updateCorrectiveAction(actionId: string, input: UpdateCorrectiveActionInput): Promise<CorrectiveAction> {
  const { data, error } = await invokeFunction<unknown>('corrective-actions', {
    method: 'PATCH',
    path: `/${actionId}`,
    body: input,
  })

  if (error) throw toApiRequestError(error, 'Failed to update corrective action')
  return toAction(data)
}

export async function linkEvidenceToAction(actionId: string, evidenceId: string): Promise<EvidenceLink> {
  const { data, error } = await invokeFunction<unknown>('corrective-actions', {
    method: 'POST',
    path: `/${actionId}/evidence:link`,
    body: { evidence_id: evidenceId },
  })

  if (error) throw toApiRequestError(error, 'Failed to link evidence')
  if (!asRecord(data)) throw new Error('Malformed evidence link response from server')
  if (typeof data.action_evidence_link_id !== 'string') throw new Error('Malformed evidence link response from server')
  if (typeof data.action_id !== 'string') throw new Error('Malformed evidence link response from server')
  if (typeof data.evidence_id !== 'string') throw new Error('Malformed evidence link response from server')
  if (typeof data.created_at !== 'string') throw new Error('Malformed evidence link response from server')

  return {
    action_evidence_link_id: data.action_evidence_link_id,
    action_id: data.action_id,
    evidence_id: data.evidence_id,
    created_at: data.created_at,
  }
}
